/** Small math, randomness and node-lifecycle helpers shared across the audio package. */

export const TAU = Math.PI * 2;

export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/** Clamp to 0..1; NaN maps to 0. */
export function clamp01(v: number): number {
  return v > 0 ? (v < 1 ? v : 1) : 0;
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** MIDI note number -> Hz (A4 = 69 = 440 Hz). */
export function mtof(midi: number): number {
  return 440 * Math.pow(2, (midi - 69) / 12);
}

/** Semitones -> frequency ratio. */
export function semis(st: number): number {
  return Math.pow(2, st / 12);
}

/** Positive modulo. */
export function mod(a: number, n: number): number {
  return ((a % n) + n) % n;
}

export function finiteOr(v: unknown, fallback: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

/** Frame-rate independent exponential approach toward `target` with time constant `tau` (s). */
export function approach(cur: number, target: number, dt: number, tau: number): number {
  if (!(tau > 0) || !(dt > 0)) return dt > 0 ? target : cur;
  return target + (cur - target) * Math.exp(-dt / tau);
}

/** mulberry32 — tiny, fast, seedable. Audio is cosmetic, so this never touches sim determinism. */
export class Rng {
  private s: number;

  constructor(seed: number) {
    this.s = seed >>> 0 || 0x6d2b79f5;
  }

  next(): number {
    this.s = (this.s + 0x6d2b79f5) >>> 0;
    let t = this.s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  range(a: number, b: number): number {
    return a + (b - a) * this.next();
  }

  /** Inclusive integer range. */
  int(lo: number, hi: number): number {
    return lo + Math.floor(this.next() * (hi - lo + 1));
  }

  chance(p: number): boolean {
    return this.next() < p;
  }

  /** Uniform in -1..1. */
  bi(): number {
    return this.next() * 2 - 1;
  }

  pick<T>(arr: readonly T[]): T {
    return arr[Math.min(arr.length - 1, Math.floor(this.next() * arr.length))] as T;
  }
}

/**
 * Wraps an AudioParam that is driven every frame. Only pushes a new setTargetAtTime event when
 * the target actually moves (keeps the automation timeline short and the per-frame cost tiny).
 */
export class Smooth {
  private last = Number.NaN;

  constructor(
    private readonly param: AudioParam,
    private readonly eps: number,
    initial?: number,
  ) {
    if (initial !== undefined) {
      param.value = initial;
      this.last = initial;
    }
  }

  to(v: number, now: number, tau: number): void {
    if (!Number.isFinite(v)) return;
    const d = Math.abs(v - this.last);
    if (d < this.eps && !(v === 0 && this.last !== 0)) return;
    this.last = v;
    this.param.setTargetAtTime(v, now, tau > 0.001 ? tau : 0.001);
  }

  get target(): number {
    return this.last;
  }
}

/** Deferred callbacks keyed on AudioContext time (processed from the engine tick). */
export class Later {
  private readonly items: { at: number; fn: () => void }[] = [];

  add(at: number, fn: () => void): void {
    this.items.push({ at, fn });
  }

  run(now: number): void {
    for (let i = this.items.length - 1; i >= 0; i--) {
      const it = this.items[i];
      if (it && it.at <= now) {
        this.items.splice(i, 1);
        try {
          it.fn();
        } catch {
          /* cleanup must never throw into the game loop */
        }
      }
    }
  }

  /** Run everything now (dispose). */
  flush(): void {
    const all = this.items.splice(0);
    for (const it of all) {
      try {
        it.fn();
      } catch {
        /* ignore */
      }
    }
  }
}

export function disconnect(node: AudioNode | null | undefined): void {
  if (!node) return;
  try {
    node.disconnect();
  } catch {
    /* already disconnected */
  }
}

export function stopSource(src: AudioScheduledSourceNode, when?: number): void {
  try {
    if (when === undefined) src.stop();
    else src.stop(when);
  } catch {
    /* not started / already stopped (old WebKit throws on a second stop) */
  }
}

export function makePanner(ctx: BaseAudioContext): StereoPannerNode | null {
  if (typeof ctx.createStereoPanner !== 'function') return null;
  try {
    return ctx.createStereoPanner();
  } catch {
    return null;
  }
}

/** Highest safe frequency for oscillators / filters on this context. */
export function nyquist(ctx: BaseAudioContext): number {
  return ctx.sampleRate * 0.45;
}

/** Copy float data into a new AudioBuffer (copyToChannel where available, else getChannelData). */
export function toBuffer(ctx: BaseAudioContext, chans: Float32Array<ArrayBuffer>[], sampleRate = ctx.sampleRate): AudioBuffer {
  const first = chans[0];
  const len = first ? Math.max(1, first.length) : 1;
  const buf = ctx.createBuffer(Math.max(1, chans.length), len, sampleRate);
  chans.forEach((c, i) => {
    if (typeof buf.copyToChannel === 'function') buf.copyToChannel(c, i);
    else buf.getChannelData(i).set(c);
  });
  return buf;
}
